import { useEffect, useState } from "react";
import { Plus, Eye, EyeOff, Copy, Trash2, Power, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSchema } from "@/context/SchemaContext";
import { Icon } from "@/lib/icons";

export default function Vault() {
  const { factories } = useSchema();
  const [keys, setKeys] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ service: "", custom: "", value: "", label: "", notes: "" });
  const [revealed, setRevealed] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/vault").then((r) => setKeys(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/vault/services").then((r) => setServices(r.data)).catch(() => {}); }, []);

  const save = async () => {
    const service = form.service === "__custom" ? form.custom.trim().toUpperCase().replace(/\s+/g, "_") : form.service;
    if (!service || !form.value) return toast.error("اختر الخدمة وأدخل المفتاح");
    const factory = services.find((s) => s.service === service)?.factory || "main";
    setBusy(true);
    try {
      await api.post("/vault", { service, value: form.value, label: form.label, notes: form.notes, factory });
      toast.success("تم حفظ المفتاح مشفراً"); setOpen(false); setForm({ service: "", custom: "", value: "", label: "", notes: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const reveal = async (k) => {
    if (revealed[k.id]) return setRevealed((r) => ({ ...r, [k.id]: null }));
    const { data } = await api.post(`/vault/${k.id}/reveal`);
    setRevealed((r) => ({ ...r, [k.id]: data.value }));
  };
  const copy = async (k) => {
    const v = revealed[k.id] || (await api.post(`/vault/${k.id}/reveal`)).data.value;
    await navigator.clipboard.writeText(v); toast.success("تم النسخ");
  };
  const toggle = async (k) => { await api.put(`/vault/${k.id}`, { active: !k.active }); load(); };
  const remove = async (k) => { if (window.confirm("حذف المفتاح نهائياً؟")) { await api.delete(`/vault/${k.id}`); load(); } };

  const groups = Object.values(factories).map((f) => ({ f, items: keys.filter((k) => k.factory === f.id) })).filter((g) => g.items.length);
  const connected = new Set(keys.filter((k) => k.active).map((k) => k.service));

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="vault-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-mono tracking-widest text-slate-500">الإدارة العامة</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-50">خزنة المفاتيح والرموز</h1>
          <p className="text-sm text-slate-400 mt-1 flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-300" /> كل المفاتيح مشفرة (AES/Fernet) — كل مصنع يحصل على مفاتيحه معزولة</p>
        </div>
        <Button data-testid="add-api-key-button" onClick={() => setOpen(true)} className="bg-amber-400 text-slate-950 hover:bg-amber-300 font-semibold rounded-xl"><Plus size={16} className="ml-1" /> إضافة مفتاح</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="integrations-overview">
        {Object.values(factories).filter((f) => f.id !== "main").map((f) => {
          const total = f.integrations?.length || 0; const done = f.integrations?.filter((s) => connected.has(s)).length || 0;
          return (
            <div key={f.id} className="panel p-4" style={{ borderColor: `${f.color}26` }}>
              <div className="flex items-center gap-2" style={{ color: f.color }}><Icon name={f.icon} size={16} /><span className="text-xs text-slate-300">{f.short}</span></div>
              <p className="font-mono text-lg text-slate-100 mt-2">{done}<span className="text-slate-500 text-sm">/{total}</span></p>
              <div className="h-1.5 rounded-full bg-white/5 mt-2 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${total ? (done / total) * 100 : 0}%`, background: f.color }} /></div>
            </div>
          );
        })}
      </div>

      {keys.length === 0 && <div className="panel p-12 text-center text-slate-500 text-sm">الخزنة فارغة — أضف مفاتيح التكاملات لتفعيل التنفيذ الفعلي في المصانع</div>}
      {groups.map(({ f, items }) => (
        <div key={f.id} className="space-y-2">
          <p className="text-xs font-mono tracking-wider flex items-center gap-2" style={{ color: f.color }}><Icon name={f.icon} size={13} /> {f.name}</p>
          <div className="grid md:grid-cols-2 gap-2">
            {items.map((k) => (
              <div key={k.id} data-testid={`api-key-card-${k.id}`} className={`panel p-4 flex items-start gap-3 ${!k.active ? "opacity-50" : ""}`}>
                <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ color: f.color, background: `${f.color}14` }}><KeyRound size={16} /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-100 truncate">{k.label}</p>
                  <p className="text-[11px] font-mono text-slate-500">{k.service}</p>
                  <p className="font-mono text-xs text-slate-300 mt-2 break-all" dir="ltr" data-testid={`api-key-value-${k.id}`}>{revealed[k.id] || k.masked}</p>
                  <p className="text-[11px] text-slate-600 mt-1">آخر استخدام: {fmtDate(k.last_used)}</p>
                </div>
                <div className="flex flex-col gap-1">
                  <button data-testid={`api-key-reveal-${k.id}`} onClick={() => reveal(k)} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-white/10">{revealed[k.id] ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                  <button data-testid={`api-key-copy-${k.id}`} onClick={() => copy(k)} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-white/10"><Copy size={14} /></button>
                  <button data-testid={`api-key-toggle-${k.id}`} onClick={() => toggle(k)} className={`h-8 w-8 grid place-items-center rounded-lg hover:bg-white/10 ${k.active ? "text-emerald-300" : "text-slate-500"}`}><Power size={14} /></button>
                  <button data-testid={`api-key-delete-${k.id}`} onClick={() => remove(k)} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-100" dir="rtl" data-testid="add-api-key-dialog">
          <DialogHeader><DialogTitle className="text-right text-amber-300">إضافة مفتاح جديد</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">الخدمة</Label>
              <select data-testid="api-key-service-select" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="dark-select">
                <option value="">— اختر الخدمة —</option>
                {services.map((s) => <option key={s.service} value={s.service}>{s.desc} ({s.service})</option>)}
                <option value="__custom">خدمة مخصصة…</option>
              </select>
            </div>
            {form.service === "__custom" && <Input data-testid="api-key-custom-service" placeholder="اسم المتغير مثل MY_SERVICE_KEY" value={form.custom} onChange={(e) => setForm({ ...form, custom: e.target.value })} className="bg-slate-900 border-slate-800 font-mono" dir="ltr" />}
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">المفتاح / التوكن</Label><Input data-testid="api-key-value-input" type="password" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="bg-slate-900 border-slate-800 font-mono" dir="ltr" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">تسمية (اختياري)</Label><Input data-testid="api-key-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="bg-slate-900 border-slate-800" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">ملاحظات</Label><Textarea data-testid="api-key-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-slate-900 border-slate-800" /></div>
          </div>
          <DialogFooter className="sm:justify-start gap-2">
            <Button data-testid="api-key-save-button" onClick={save} disabled={busy} className="bg-amber-400 text-slate-950 hover:bg-amber-300 font-semibold">{busy ? "جارٍ الحفظ..." : "حفظ مشفراً"}</Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>إلغاء</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
